import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Calendar, Radio, ExternalLink, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

/**
 * Public BroadcastFlow-hosted blog. Renders approved + published news_stories
 * for a station. Two routes:
 *   /blog/:station_id           — paginated index
 *   /blog/:station_id/:story_id — single story page with prev/next nav
 */
export default function PublicBlog() {
  const { station_id, story_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true); setError(null);
    const url = story_id ? `/blog/${station_id}/${story_id}` : `/blog/${station_id}?page=${page}&page_size=12`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load this blog"))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [station_id, story_id, page]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-bg text-ink-muted font-mono text-xs">LOADING…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg text-ink-secondary p-8">
        <div className="max-w-md text-center">
          <Radio className="h-10 w-10 mx-auto text-brand-amber mb-3" />
          <h1 className="font-display text-2xl font-black mb-2">Blog unavailable</h1>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const { station } = data;

  return (
    <div className="min-h-screen bg-bg text-ink-primary">
      {/* Station header banner */}
      <header className="border-b border-line bg-bg-surface">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-4">
          {station.logo_url ? (
            <img src={station.logo_url} alt={station.name} className="h-12 w-12 rounded-lg object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-brand-amber/15 text-brand-amber grid place-items-center">
              <Radio className="h-6 w-6" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <Link to={`/blog/${station_id}`} className="font-display text-2xl font-black tracking-tight hover:text-cyan-300 transition" data-testid="blog-station-name">
              {station.name}
            </Link>
            <div className="text-xs text-ink-muted font-mono mt-0.5">
              {station.tagline || station.genre || "Live from BroadcastFlow AI"}
            </div>
          </div>
          {station.website && (
            <a
              href={station.website}
              target="_blank"
              rel="noreferrer"
              data-testid="blog-station-website"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-cyan-300 transition"
            >
              Visit station <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {story_id ? <StoryView data={data} navigate={navigate} station_id={station_id} /> : <BlogIndex data={data} page={page} setPage={setPage} station_id={station_id} />}
      </main>

      <footer className="border-t border-line bg-bg-surface mt-16">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center space-y-2">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
            // POWERED BY <span className="text-brand-amber">BROADCASTFLOW AI</span>
          </div>
          <div className="text-[11px] text-ink-muted" data-testid="blog-image-sources">
            Image sources: photography provided by{" "}
            <a
              href="https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"
              target="_blank"
              rel="noreferrer"
              className="text-sky-400 hover:underline"
            >
              Unsplash
            </a>
            {" "}— individual photographers are credited on each photo.
          </div>
        </div>
      </footer>
    </div>
  );
}

function BlogIndex({ data, page, setPage, station_id }) {
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-2">// THE NEWSROOM</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Latest stories.</h1>
        {data.station.description && (
          <p className="mt-3 text-ink-secondary max-w-2xl">{data.station.description}</p>
        )}
      </div>

      {data.items.length === 0 ? (
        <div className="rounded-2xl border border-line bg-bg-surface p-8 text-center text-ink-muted text-sm" data-testid="blog-empty">
          No stories published yet. Check back soon.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-5" data-testid="blog-index">
          {data.items.map((s) => (
            <li key={s.story_id}>
              <Link
                to={`/blog/${station_id}/${s.story_id}`}
                data-testid={`blog-card-${s.story_id}`}
                className="block rounded-2xl border border-line bg-bg-surface p-5 hover:border-brand-amber/50 transition group"
              >
                {s.image_url && (
                  <img src={s.image_url} alt="" className="w-full h-44 object-cover rounded-lg mb-4" />
                )}
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">
                  <Calendar className="h-3 w-3" /> {new Date(s.created_at).toLocaleDateString()}
                  {s.language && s.language !== "en" && (
                    <span className="rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.5 ml-auto uppercase tracking-wider">
                      {s.language}
                    </span>
                  )}
                </div>
                <h2 className="font-display text-lg font-bold leading-snug mb-2 group-hover:text-cyan-300 transition">
                  {s.title}
                </h2>
                {s.summary && (
                  <p className="text-sm text-ink-secondary line-clamp-3">{s.summary}</p>
                )}
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-cyan-300">
                  Read story <ChevronRight className="h-3 w-3" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2 text-xs">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            data-testid="blog-prev-page"
            className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-2 hover:text-cyan-300 transition disabled:opacity-40"
          >
            <ArrowLeft className="h-3 w-3" /> Newer
          </button>
          <span className="font-mono text-ink-muted px-3">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            data-testid="blog-next-page"
            className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-2 hover:text-cyan-300 transition disabled:opacity-40"
          >
            Older <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </>
  );
}

function StoryView({ data, navigate, station_id }) {
  const { story, prev, next } = data;
  const bodyHtml = story.body_html || (story.body || "")
    .split(/\n\s*\n/)
    .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
  return (
    <article data-testid="blog-story-view">
      <Link
        to={`/blog/${station_id}`}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-cyan-300 transition mb-6"
        data-testid="blog-back-to-index"
      >
        <ArrowLeft className="h-3 w-3" /> All stories
      </Link>

      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-brand-amber mb-3">
        <Calendar className="h-3 w-3" />
        <time>{new Date(story.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}</time>
        {story.language && story.language !== "en" && (
          <span className="rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.5">
            {story.language.toUpperCase()}
          </span>
        )}
        {(story.tags || []).map((t) => (
          <span key={t} className="rounded-full bg-bg-elevated border border-line px-1.5 py-0.5 text-ink-secondary">{t}</span>
        ))}
      </div>

      <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-tight mb-5" data-testid="blog-story-title">
        {story.title}
      </h1>
      {story.summary && (
        <p className="text-lg text-ink-secondary leading-relaxed mb-8 italic">{story.summary}</p>
      )}
      {story.image_url && (
        <figure className="mb-8">
          <img src={story.image_url} alt={story.image_credit?.alt || ""} className="w-full rounded-2xl max-h-[480px] object-cover" />
          {story.image_credit?.photographer && (
            <figcaption className="mt-2 text-[11px] text-ink-muted text-right" data-testid="blog-story-image-credit">
              Photo by{" "}
              <a href={story.image_credit.photographer_url} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                {story.image_credit.photographer}
              </a>
              {" "}on{" "}
              <a
                href={story.image_credit.unsplash_url || "https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"}
                target="_blank" rel="noreferrer" className="text-sky-400 hover:underline"
              >
                Unsplash
              </a>
            </figcaption>
          )}
        </figure>
      )}
      {story.video_url && (
        <video src={story.video_url} controls className="w-full rounded-2xl mb-8" data-testid="blog-story-video" />
      )}
      {story.audio_url && (
        <audio src={story.audio_url} controls className="w-full mb-8" data-testid="blog-story-audio" />
      )}

      <div
        className="prose prose-invert max-w-none text-ink-primary leading-relaxed [&_p]:mb-5 [&_a]:text-cyan-300 [&_a:hover]:text-brand-amber"
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
        data-testid="blog-story-body"
      />

      <nav className="mt-12 pt-8 border-t border-line grid sm:grid-cols-2 gap-3" data-testid="blog-story-nav">
        {prev && (
          <button
            onClick={() => navigate(`/blog/${station_id}/${prev.story_id}`)}
            data-testid="blog-story-prev"
            className="text-left rounded-xl border border-line bg-bg-surface p-4 hover:border-brand-amber/50 transition"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1 inline-flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Previous
            </div>
            <div className="text-sm font-semibold line-clamp-2">{prev.title}</div>
          </button>
        )}
        {next && (
          <button
            onClick={() => navigate(`/blog/${station_id}/${next.story_id}`)}
            data-testid="blog-story-next"
            className="text-right rounded-xl border border-line bg-bg-surface p-4 hover:border-brand-amber/50 transition sm:col-start-2"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1 inline-flex items-center gap-1 justify-end w-full">
              Next <ArrowRight className="h-3 w-3" />
            </div>
            <div className="text-sm font-semibold line-clamp-2">{next.title}</div>
          </button>
        )}
      </nav>
    </article>
  );
}
